import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Link } from 'react-scroll';

const MockDashboard = () => (
  <Box sx={{
    width: '100%', borderRadius: '16px',
    bgcolor: '#FFFFFF',
    border: '1px solid rgba(0,0,0,0.08)',
    overflow: 'hidden',
    boxShadow: '0 32px 80px rgba(99,102,241,0.12), 0 8px 24px rgba(0,0,0,0.08)',
    animation: 'float 6s ease-in-out infinite',
  }}>
    {/* Window bar */}
    <Box sx={{ bgcolor: '#F3F4F6', px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#FF5F57' }} />
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#FFBD2E' }} />
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#28C840' }} />
      <Box sx={{ flex: 1, mx: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: '4px', height: 20, display: 'flex', alignItems: 'center', px: 1.5 }}>
        <Typography sx={{ fontSize: '0.62rem', color: '#8B92A5' }}>app.infynihub.com/dashboard</Typography>
      </Box>
    </Box>
    <Box sx={{ p: 2.5 }}>
      {/* Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1.5, mb: 2 }}>
        {[
          { label: 'Open Tickets', value: '24', color: '#6366F1', bg: '#EEF2FF' },
          { label: 'Resolved', value: '142', color: '#16A34A', bg: '#F0FDF4' },
          { label: 'Pending', value: '8', color: '#D97706', bg: '#FFFBEB' },
        ].map((s) => (
          <Box key={s.label} sx={{ bgcolor: s.bg, borderRadius: '8px', p: 1.5, border: `1px solid ${s.color}20` }}>
            <Typography sx={{ fontSize: '0.6rem', color: '#6B7280', mb: 0.5 }}>{s.label}</Typography>
            <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</Typography>
          </Box>
        ))}
      </Box>
      {/* Ticket rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {[
          { id: '#1042', vehicle: 'Acme Corp — Motor Unit', status: 'In Progress', color: '#6366F1', bg: '#EEF2FF' },
          { id: '#1041', vehicle: 'BuildRight — Pump A7', status: 'Resolved', color: '#16A34A', bg: '#F0FDF4' },
          { id: '#1040', vehicle: 'TechFlow — Control Board', status: 'Pending', color: '#D97706', bg: '#FFFBEB' },
          { id: '#1039', vehicle: 'Nexus Ltd — Sensor Unit', status: 'In Progress', color: '#6366F1', bg: '#EEF2FF' },
        ].map((t) => (
          <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#F9FAFB', borderRadius: '6px', px: 1.5, py: 1, border: '1px solid rgba(0,0,0,0.05)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontSize: '0.6rem', color: '#9CA3AF', fontFamily: 'monospace' }}>{t.id}</Typography>
              <Typography sx={{ fontSize: '0.65rem', color: '#111827', fontWeight: 600 }}>{t.vehicle}</Typography>
            </Box>
            <Box sx={{ px: 1, py: 0.3, borderRadius: '4px', bgcolor: t.bg }}>
              <Typography sx={{ fontSize: '0.55rem', color: t.color, fontWeight: 700 }}>{t.status}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
      {/* Mini chart */}
      <Box sx={{ mt: 2, bgcolor: '#F9FAFB', borderRadius: '8px', p: 1.5, border: '1px solid rgba(0,0,0,0.05)' }}>
        <Typography sx={{ fontSize: '0.6rem', color: '#9CA3AF', mb: 1 }}>Weekly Activity</Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 40 }}>
          {[45, 70, 55, 85, 65, 90, 100].map((h, i) => (
            <Box key={i} sx={{ flex: 1, height: `${h}%`, borderRadius: '3px 3px 0 0', bgcolor: i === 6 ? '#6366F1' : '#E0E7FF' }} />
          ))}
        </Box>
      </Box>
    </Box>
  </Box>
);

const Hero = () => (
  <Box className="grid-bg" sx={{
    minHeight: '100vh', bgcolor: '#FFFFFF',
    display: 'flex', alignItems: 'center',
    position: 'relative', overflow: 'hidden',
    pt: { xs: '140px', md: '150px' },
    pb: { xs: 8, md: 4 },
  }}>
    <Box sx={{ position: 'absolute', top: '10%', left: '35%', width: 700, height: 700, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 65%)', pointerEvents: 'none', animation: 'pulse-glow 8s ease-in-out infinite' }} />

    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 6, md: 10 }, alignItems: 'center' }}>

        {/* Left */}
        <Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: '100px', px: 2, py: 0.75, mb: 4 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6366F1' }} />
            <Typography sx={{ color: '#4F46E5', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Digital Transformation for SMEs
            </Typography>
          </Box>

          <Typography sx={{
            fontSize: { xs: '2.6rem', sm: '3.2rem', md: '4rem' },
            fontWeight: 900, lineHeight: 1.08, letterSpacing: '-0.03em', mb: 3, color: '#0F1117',
          }}>
            Your Business{' '}
            <Box component="span" sx={{ color: '#6366F1' }}>Deserves Better</Box>
            {' '}Than Excel.
          </Typography>

          <Typography sx={{ color: '#6B7280', fontSize: { xs: '1rem', md: '1.05rem' }, lineHeight: 1.85, mb: 5, maxWidth: 480 }}>
            We build purpose-built software and provide end-to-end IT services that replace manual workflows — so your team spends time on work that actually matters.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 5 }}>
            <Link to="contact" smooth duration={600} offset={-80}>
              <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} sx={{
                bgcolor: '#6366F1', color: '#fff', px: 4, py: 1.6,
                fontWeight: 700, fontSize: '0.95rem', borderRadius: '8px', textTransform: 'none',
                boxShadow: '0 4px 20px rgba(99,102,241,0.35)',
                '&:hover': { bgcolor: '#5254CC', transform: 'translateY(-2px)', boxShadow: '0 8px 28px rgba(99,102,241,0.45)' },
                transition: 'all 0.25s ease',
              }}>
                Start the Conversation
              </Button>
            </Link>
            <Link to="solutions" smooth duration={600} offset={-80}>
              <Button variant="outlined" size="large" sx={{
                borderColor: '#E5E7EB', color: '#374151',
                px: 4, py: 1.6, fontWeight: 600, fontSize: '0.95rem',
                borderRadius: '8px', textTransform: 'none',
                '&:hover': { borderColor: '#6366F1', color: '#6366F1', bgcolor: '#EEF2FF' },
                transition: 'all 0.25s ease',
              }}>
                See Our Products
              </Button>
            </Link>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            {[
              'Replace Excel & paper with real software',
              'Full IT stack — from strategy to hardware',
              'Built specifically for SMEs',
            ].map((t) => (
              <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CheckCircleOutlineIcon sx={{ color: '#6366F1', fontSize: 18, flexShrink: 0 }} />
                <Typography sx={{ color: '#6B7280', fontSize: '0.88rem' }}>{t}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Right */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative' }}>
          <Box sx={{ position: 'absolute', inset: -30, background: 'radial-gradient(ellipse at center, rgba(99,102,241,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <MockDashboard />
        </Box>
      </Box>
    </Container>
  </Box>
);

export default Hero;
